import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  TableContainer,
  Icon,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FiMonitor, FiArrowLeft, FiCheck } from "react-icons/fi";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import axios from "axios";
import { api_url } from "../../url";
import Pagination from "../../Components/Common/Pagination";

const SIDEBAR_WIDTH = "240px";

const PendingDeposite = () => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const toast = useToast();

  const fetchPendingDeposits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${api_url}/deposits/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "pending", page, limit },
      });
      if (res.data.success) {
        setDeposits(res.data.deposits);
        setTotalPages(res.data.pagination.pages);
        setTotalEntries(res.data.pagination.total);
      }
    } catch (error) {
      toast({ title: "Error fetching deposits", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingDeposits();
  }, [page, limit]);

  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${api_url}/deposits/complete/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Deposit approved", status: "success" });
      setSelectedDeposit(null);
      fetchPendingDeposits();
    } catch (error) {
      toast({ title: "Approval failed", status: "error" });
    }
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  if (loading && deposits.length === 0) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {selectedDeposit ? (
          <Box maxW="800px" mx="auto">
            <Flex align="center" mb={6} justify="space-between">
              <Text fontSize="2xl" fontWeight="600" color="#344767">
                Deposit Details
              </Text>
              <Button leftIcon={<FiArrowLeft />} onClick={() => setSelectedDeposit(null)}>
                Back
              </Button>
            </Flex>
            <Box bg="white" p={6} borderRadius="12px" shadow="sm">
              <Table variant="simple">
                <Tbody>
                  <Tr>
                    <Td>User</Td>
                    <Td textAlign="right" fontWeight="bold">
                      {selectedDeposit.user?.username} ({selectedDeposit.user?.userId})
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Amount</Td>
                    <Td textAlign="right" fontWeight="bold">₹{selectedDeposit.amount}</Td>
                  </Tr>
                  <Tr>
                    <Td>Transaction ID</Td>
                    <Td textAlign="right">{selectedDeposit.transactionId}</Td>
                  </Tr>
                  <Tr>
                    <Td>Date</Td>
                    <Td textAlign="right">{new Date(selectedDeposit.createdAt).toLocaleString()}</Td>
                  </Tr>
                </Tbody>
              </Table>
              <Button
                mt={6}
                colorScheme="green"
                w="full"
                leftIcon={<FiCheck />}
                onClick={() => handleApprove(selectedDeposit._id)}
              >
                Approve & Credit Balance
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Text fontSize="2xl" fontWeight="700" color="#344767" mb={8}>
              Pending Deposits
            </Text>
            <Box bg="white" borderRadius="12px" shadow="sm" overflow="hidden">
              {loading ? (
                <Flex justify="center" p={10}><Spinner /></Flex>
              ) : (
                <TableContainer>
                  <Table variant="simple">
                    <Thead bg="#4c3cff">
                      <Tr>
                        <Th color="white">Gateway | Transaction</Th>
                        <Th color="white">Initiated</Th>
                        <Th color="white">User</Th>
                        <Th color="white">Amount</Th>
                        <Th color="white">Status</Th>
                        <Th color="white">Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {deposits.map((d) => (
                        <Tr key={d._id}>
                          <Td>
                            <Text fontWeight="bold">IMB Gateway</Text>
                            <Text fontSize="xs">{d.transactionId}</Text>
                          </Td>
                          <Td fontSize="sm">
                            {new Date(d.createdAt).toLocaleString()}
                          </Td>
                          <Td>
                            <Text fontWeight="bold">{d.user?.username}</Text>
                            <Text fontSize="xs">{d.user?.userId}</Text>
                          </Td>
                          <Td fontWeight="bold">₹{d.amount}</Td>
                          <Td>
                            <Badge colorScheme="orange">PENDING</Badge>
                          </Td>
                          <Td>
                            <Button size="sm" leftIcon={<FiMonitor />} onClick={() => setSelectedDeposit(d)}>
                              Details
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                      {deposits.length === 0 && (
                        <Tr>
                          <Td colSpan={6} textAlign="center">No pending deposits found</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Pagination */}
            <Pagination 
              currentPage={page}
              totalPages={totalPages}
              totalEntries={totalEntries}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default PendingDeposite;
